// src/components/dashboard/HomeLandingView.js

import React from "react";
import { Box, Typography } from "@mui/material";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";

// Exact matching outlined icons
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";

const clinicalFeatures = [
  {
    icon: <DescriptionOutlinedIcon sx={{ fontSize: "1.45rem", color: "#0B2540" }} />,
    title: "Specialized Documentation",
    description:
      "Tailored workflows for ARMHS (ITPs, Progress Notes), Doula (Prenatal/Postpartum), and RN services.",
  },
  {
    icon: <VerifiedUserOutlinedIcon sx={{ fontSize: "1.45rem", color: "#0B2540" }} />,
    title: "MN DHS & HIPAA Compliant",
    description:
      "Secure, end-to-end encryption meeting all Minnesota statutes and documentation standards.",
  },
  {
    icon: <ReceiptLongOutlinedIcon sx={{ fontSize: "1.45rem", color: "#0B2540" }} />,
    title: "Billing & Claims",
    description:
      "Streamlined MN Medical Assistance (MA) billing and private insurance claims management.",
  },
  {
    icon: <TouchAppOutlinedIcon sx={{ fontSize: "1.45rem", color: "#0B2540" }} />,
    title: "Intuitive Interface",
    description:
      "Designed to reduce administrative burden so you can focus on patient care.",
  },
];

const HomeLandingView = () => {
  return (
    <Box sx={{ width: "100%", pb: 6 }}>
      {/* HERO BANNER */}
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          borderRadius: "16px",
          background: "linear-gradient(90deg, #112D47 0%, #173E62 50%, #1F4E79 100%)",
          color: "#FFFFFF",
          textAlign: "center",
          py: { xs: 4, md: 5 },
          px: 3,
          mb: 4,
          boxShadow: "0 10px 30px rgba(11, 37, 64, 0.15)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.16)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.2,
          }}
        >
          <CelebrationOutlinedIcon sx={{ color: "#FFFFFF", fontSize: "1.45rem" }} />
        </Box>

        <Typography
          sx={{
            fontSize: { xs: "2rem", md: "2.6rem" },
            fontWeight: 800,
            letterSpacing: "-0.5px",
            lineHeight: 1.15,
            mb: 2,
            color: "#FFFFFF",
          }}
        >
          Welcome, NeuroEZ
        </Typography>

        <Box
          sx={{
            width: 45,
            height: "1px",
            backgroundColor: "rgba(255, 255, 255, 0.35)",
            mb: 2,
          }}
        />

        <Typography
          sx={{
            fontSize: "0.95rem",
            color: "rgba(255, 255, 255, 0.9)",
            fontWeight: 500,
            letterSpacing: "0.2px",
          }}
        >
          Today is Tuesday, <strong style={{ color: "#FFFFFF" }}>September 15, 2026</strong>
        </Typography>
      </Box>

      {/* SECTION HEADER */}
      <Box sx={{ textAlign: "center", maxWidth: "820px", mx: "auto", px: 2, mb: 4 }}>
        <Typography
          sx={{
            fontSize: { xs: "1.5rem", md: "1.9rem" },
            fontWeight: 800,
            color: "#0B2540",
            letterSpacing: "-0.4px",
            mb: 1.2,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            ".dark-theme &, [data-theme='dark'] &, .dark &": {
              color: "#FFFFFF",
            },
          }}
        >
          Tailored EHR for Modern Healthcare
        </Typography>

        <Typography
          sx={{
            fontSize: "0.88rem",
            color: "#64748B",
            lineHeight: 1.6,
            fontWeight: 450,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            ".dark-theme &, [data-theme='dark'] &, .dark &": {
              color: "#94A3B8",
            },
          }}
        >
          NeuroEZ is an inclusive, compliant, and intuitive platform designed to streamline workflows for{" "}
          <strong style={{ color: "#334155" }}>ARMHS, Doula, and RN services</strong>. We help you meet Minnesota DHS regulations while enhancing service delivery.
        </Typography>
      </Box>

      {/* =========================================================
          EXACT 2x2 GRID (2 UPAR, EXACT 2 NEECHE)
         ========================================================= */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "1150px",
          mx: "auto",
          px: { xs: 1, sm: 2 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, // Desktop par exact 2 columns
          gap: "24px", // Equal horizontal & vertical spacing
        }}
      >
        {clinicalFeatures.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "20px", // Screenshot match rounded corner
              border: "1px solid #EEF3F8",
              boxShadow: "0 4px 18px rgba(15, 37, 64, 0.04)",
              p: { xs: 3, sm: 3.5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              boxSizing: "border-box",
              minHeight: "185px",
              transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
              "&:hover": {
                transform: "translateY(-3px)",
                borderColor: "#D8E4F0",
                boxShadow: "0 10px 24px rgba(15, 37, 64, 0.08)",
              },
              ".dark-theme &, [data-theme='dark'] &, .dark &": {
                backgroundColor: "#0B2540",
                borderColor: "rgba(255, 255, 255, 0.08)",
              },
            }}
          >
            {/* Soft Circular Icon Badge */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#E8F2FA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2.2,
                ".dark-theme &, [data-theme='dark'] &, .dark &": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                  "& svg": { color: "#FFFFFF" },
                },
              }}
            >
              {item.icon}
            </Box>

            {/* Card Title */}
            <Typography
              sx={{
                fontSize: "1.18rem",
                fontWeight: 800,
                color: "#0B2540",
                letterSpacing: "-0.2px",
                lineHeight: 1.3,
                mb: 1.2,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                ".dark-theme &, [data-theme='dark'] &, .dark &": {
                  color: "#FFFFFF",
                },
              }}
            >
              {item.title}
            </Typography>

            {/* Card Description */}
            <Typography
              sx={{
                fontSize: "0.91rem",
                color: "#64748B",
                lineHeight: 1.6,
                fontWeight: 450,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                ".dark-theme &, [data-theme='dark'] &, .dark &": {
                  color: "#94A3B8",
                },
              }}
            >
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default HomeLandingView;